// Property-modal ↔ browser-history coordination (#501).
//
// Background: opening a property used to call `navigate(?property=id,
// { replace: true })`, which mutates the URL WITHOUT adding a history
// entry. On Android, the hardware back button then had nothing to pop for
// the modal, so it exited the site to the home screen instead of closing
// the property and returning to the map.
//
// Fix model: treat an open property modal as exactly ONE pushed history
// entry. Opening from the map/list PUSHes that entry; closing pops it
// (browser back, the X button, and Escape all funnel through the same
// pop). Switching directly from one property to another REPLACEs so we
// never stack multiple modal entries. A property opened via a deep link
// (`?property=` already in the URL on load) has no pushed entry, so its
// close just strips the param.
//
// These helpers are pure so the push/replace/back decision is unit-tested
// independently of App.tsx (which can't be rendered headlessly because of
// its ArcGIS / Supabase dependencies).

export type ModalNav =
  | { kind: "push"; url: string }
  | { kind: "replace"; url: string }
  | { kind: "back" };

// planOpenNav: what navigation to perform when a property modal opens.
// `alreadyPushed` is true when a modal history entry already exists (the
// user is switching from one open property to another) — in that case we
// replace so the back stack keeps exactly one extra entry.
export function planOpenNav(
  pathname: string,
  search: string,
  propertyId: string,
  alreadyPushed: boolean,
): { kind: "push" | "replace"; url: string } {
  const params = new URLSearchParams(search);
  params.set("property", propertyId);
  const url = `${pathname}?${params.toString()}`;
  return alreadyPushed ? { kind: "replace", url } : { kind: "push", url };
}

// planCloseNav: what navigation to perform when a property modal closes.
// If we pushed an entry, pop it (kind: "back") so a subsequent back press
// leaves the map as the user expects. Otherwise (deep-link open) strip the
// `?property=` param in place, preserving any other query params.
export function planCloseNav(
  pathname: string,
  search: string,
  alreadyPushed: boolean,
): ModalNav {
  if (alreadyPushed) return { kind: "back" };
  const params = new URLSearchParams(search);
  params.delete("property");
  const qs = params.toString();
  return { kind: "replace", url: qs ? `${pathname}?${qs}` : pathname };
}
